import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { defaultLanguage, languages } from "../../constants";
import { loadLanguage } from "../../store/persistence";

import untypedTranslations from "../../translations_data.json";

type TranslationsByLanguage = Record<string, string>;

const typedTranslations: Record<string, TranslationsByLanguage | string> =
  untypedTranslations;

/**
 * Build a translations dictionary for a particular language.
 *
 * This maps the translation key (the language-invariant descriptor of the piece
 * of text) to the version of that text in the given language.
 */
const buildTranslations = (shortLanguage: string): TranslationsByLanguage => {
  const result: TranslationsByLanguage = {};

  for (const key in typedTranslations) {
    const translationsValue = typedTranslations[key];
    if (translationsValue === undefined) continue;

    // Some values (eg. numeric ones) only have one translation
    if (typeof translationsValue === "string") {
      result[key] = translationsValue;
    } else {
      // Fall back to the key for missing AND empty-string translations,
      // so untranslated locales never render blank UI text.
      const translation = translationsValue[shortLanguage];
      result[key] =
        translation === undefined || translation === "" ? key : translation;
    }
  }

  return result;
};

export const setupTranslations = () => {
  void i18n.use(initReactI18next).init({
    resources: Object.fromEntries(
      Object.keys(languages).map((language) => [
        language,
        Object.assign({
          translation: buildTranslations(language.slice(0, 2).toUpperCase()),
        }),
      ])
    ),
    lng: loadLanguage() ?? defaultLanguage,
    fallbackLng: defaultLanguage,
    fallbackNS: "translation",
    interpolation: {
      escapeValue: false,
    },
  });

  i18n.use({
    type: "postProcessor",
    name: "capitalise",
    process: function (value: string) {
      if (typeof value !== "string" || !value.length) return value;
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
  });
};
